import React from "react";
import {
  Box,
  Grid,
  SpaceBetween,
  Link,
  ExpandableSection,
  Table,
  ColumnLayout,
  Container,
} from "@amzn/awsui-components-react";
import { TEMPLATE_APP, TEMPLATE_APP_SOURCE_DATA_TABLES, TEMPLATE_APP_CHART_OF_ACCOUNTS } from "../Constants";
import CustomMultiHybridInputsForSourceTables from "./CustomMultiHybridInputsForSourceTables";
import CustomMultiHybridInputsWithPK from "./CustomMultiHybridInputsWithPK";
import CustomMultiSelect from "./CustomMultiSelect";
import CustomInput from "./CustomInput";

const OperationalDetailsTile = ({
  isEditable,
  getValue,
  updateValue,
  getSelectFieldValues,
  getMultiSelectedOptions,
  shouldShowMandatory,
  usecaseInputSourceTypes,
  usecaseAutomationTools
}) => {
  const renderMandatoryIndicator = () => (
    <Box color="text-status-error" fontSize="heading-l" fontWeight="bold">
      *
    </Box>
  );

  const renderSourceDataTablesSection = () => (
    <ExpandableSection
      defaultExpanded={true}
      headerText="Source data tables"
      variant="borderless"
    >
      {isEditable(TEMPLATE_APP.SOURCE_DATA_TABLES) ? (
        <CustomMultiHybridInputsForSourceTables 
          field={TEMPLATE_APP.SOURCE_DATA_TABLES}
          updateValue={updateValue} 
          getValues={getValue} 
        />
      ) : (
        <SpaceBetween size="s">
          {getValue(TEMPLATE_APP.SOURCE_DATA_TABLES) ? (
            <Table
              columnDefinitions={[
                {
                  id: "upstream_source",
                  header: "Upstream source",
                  cell: item => item[TEMPLATE_APP_SOURCE_DATA_TABLES.UPSTREAM_SOURCE],
                  isRowHeader: true
                },
                {
                  id: "source_schema",
                  header: "Schema",
                  cell: item => item[TEMPLATE_APP_SOURCE_DATA_TABLES.SCHEMA],
                  sortingField: "alt"
                },
                {
                  id: "source_database",
                  header: "Database",
                  cell: item => item[TEMPLATE_APP_SOURCE_DATA_TABLES.DATABASE],
                  sortingField: "alt"
                },
                {
                  id: "source_table",
                  header: "Table",
                  cell: item => item[TEMPLATE_APP_SOURCE_DATA_TABLES.TABLE],
                  sortingField: "alt"
                },
                {
                  id: "description",
                  header: "Description",
                  cell: item => item[TEMPLATE_APP_SOURCE_DATA_TABLES.DESCRIPTION],
                  sortingField: "alt"
                }
              ]}
              items={getValue(TEMPLATE_APP.SOURCE_DATA_TABLES)}
              loadingText="Loading source data tables"
              sortingDisabled
              empty={
                <Box margin={{ vertical: "xs" }} textAlign="center" color="inherit">
                  <SpaceBetween size="m">
                    <Box variant="strong">No resources</Box>
                  </SpaceBetween>
                </Box>
              }
              variant="embedded"
            />
          ) : (
            <Box textAlign="left" color="text-body-secondary">
              No source data tables configured
            </Box>
          )}
        </SpaceBetween>
      )}
    </ExpandableSection>
  );

  const renderDocumentSection = (field, title, placeholder, linkText) => (
    <SpaceBetween size="xs">
      <Box variant="h4">{title}</Box>
      {isEditable(field) ? (
        field === TEMPLATE_APP.PDD ? (
          <Grid gridDefinition={[{ colspan: 11 }, { colspan: 1 }]}>
            <CustomInput 
              field={field} 
              placeholder={placeholder} 
              updateValue={updateValue} 
              getValue={getValue}
            />
            {shouldShowMandatory() && renderMandatoryIndicator()}
          </Grid>
        ) : (
          <CustomInput 
            field={field} 
            placeholder={placeholder} 
            updateValue={updateValue} 
            getValue={getValue}
          />
        )
      ) : (
        <Box>
          {getValue(field) ? (
            <Link href={getValue(field)} external>
              {linkText}
            </Link>
          ) : (
            <Box color="text-body-secondary">
              Not Available
            </Box>
          )}
        </Box>
      )}
    </SpaceBetween>
  );

  const renderGLAccountsSection = () => (
    <ExpandableSection
      defaultExpanded={true}
      headerText="GL accounts"
      variant="borderless"
    >
      {isEditable(TEMPLATE_APP.CHART_OF_ACCOUNT) ? (
        <CustomMultiHybridInputsWithPK 
          field={TEMPLATE_APP.CHART_OF_ACCOUNT} 
          subKeys={[{ subKey: TEMPLATE_APP_CHART_OF_ACCOUNTS.FPA_DIM_ACCOUNTS, placeholder: 'COA # only' }]}
          pk={TEMPLATE_APP_CHART_OF_ACCOUNTS.CHART_OF_ACCOUNTS_PK} 
          updateValue={updateValue} 
          getValues={getValue} 
        />
      ) : (
        <SpaceBetween size="xs">
          {getValue(TEMPLATE_APP.CHART_OF_ACCOUNT) && getValue(TEMPLATE_APP.CHART_OF_ACCOUNT).length > 0 ? (
            getValue(TEMPLATE_APP.CHART_OF_ACCOUNT).map((item, index) => (
              item.hasOwnProperty(TEMPLATE_APP_CHART_OF_ACCOUNTS.FPA_DIM_ACCOUNTS) ? (
                <Box key={index} variant="p">
                  {item[TEMPLATE_APP_CHART_OF_ACCOUNTS.FPA_DIM_ACCOUNTS]}
                </Box>
              ) : null
            ))
          ) : (
            <Box color="text-body-secondary">
              No GL accounts configured
            </Box>
          )}
        </SpaceBetween>
      )}
    </ExpandableSection>
  );

  const renderInputSourceTypesSection = () => (
    <ExpandableSection
      defaultExpanded={true}
      headerText="Input Source Types"
      variant="borderless"
    >
      {isEditable(TEMPLATE_APP.INPUT_SOURCE_TYPE) ? (
        <CustomMultiSelect 
          field={TEMPLATE_APP.INPUT_SOURCE_TYPE}
          updateValue={updateValue} 
          getValues={getSelectFieldValues} 
          getSelectedOptions={getMultiSelectedOptions} 
        />
      ) : (
        <SpaceBetween size="xs">
          {usecaseInputSourceTypes && usecaseInputSourceTypes.length > 0 ? (
            usecaseInputSourceTypes.map((item) => (
              <Box key={item} variant="p">
                {item}
              </Box>
            ))
          ) : (
            <Box color="text-body-secondary">
              No input source types configured
            </Box>
          )}
        </SpaceBetween>
      )}
    </ExpandableSection>
  );

  const renderAutomationToolsSection = () => (
    <ExpandableSection
      defaultExpanded={true}
      headerText="Automation Tools"
      variant="borderless"
    >
      {isEditable(TEMPLATE_APP.AUTOMATION_TOOLS) ? (
        <Grid gridDefinition={[{ colspan: 11 }, { colspan: 1 }]}>
          <CustomMultiSelect 
            field={TEMPLATE_APP.AUTOMATION_TOOLS}
            updateValue={updateValue} 
            getValues={getSelectFieldValues} 
            getSelectedOptions={getMultiSelectedOptions} 
          />
          {shouldShowMandatory() && renderMandatoryIndicator()}
        </Grid>
      ) : (
        <SpaceBetween size="xs">
          {usecaseAutomationTools && usecaseAutomationTools.length > 0 ? (
            usecaseAutomationTools.map((item) => (
              <Box key={item} variant="p">
                {item}
              </Box>
            ))
          ) : (
            <Box color="text-body-secondary">
              No automation tools configured
            </Box>
          )}
        </SpaceBetween>
      )}
    </ExpandableSection>
  );

  return (
    <Box padding={{ left: "l" }}>
      <SpaceBetween size="l">
        <ColumnLayout columns={1}>
        {renderSourceDataTablesSection()}
      </ColumnLayout>
      
      <ColumnLayout columns={3}>
        {renderDocumentSection(TEMPLATE_APP.ARD, "Accounting Requirements Document (ARD)", "ARD", "ARD")}
        {renderDocumentSection(TEMPLATE_APP.PDD, "Project Design Document (PDD)", "PDD", "PDD")}
        {renderDocumentSection(TEMPLATE_APP.TDD, "Technical Design Document (TDD)", "TDD", "TDD")}
        
        {renderGLAccountsSection()}
        
        {renderInputSourceTypesSection()}
        
        {renderAutomationToolsSection()}
        </ColumnLayout>
      </SpaceBetween>
    </Box>
  );
};

export default OperationalDetailsTile;
